import { Badge } from '@civilgrid/ui/components';
import { cn, getBillingTierColorClass } from '@civilgrid/ui/utils';
import { ProjectFeature } from '@civilgrid/utils/src/common/featureAccess';
import { useNavigate, useParams } from '@tanstack/react-router';
import { type ReactNode, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { hasFeatureAccess } from '~/authorizations/FeatureFlags';
import { returnProjectTypeSpecificProps } from '~/common/projectUtils';
import type { Project } from '~/common/types';
import { useConflictNotificationsFlag } from '~/hooks/useConflictNotificationsEnabled';
import { useExportLabels } from '~/hooks/useExportTerminologyEnabled';
import { usePermitPdfGenerationEnabled } from '~/hooks/usePermitPdfGenerationEnabled';
import LeftNav from '~/pages/common/LeftNav';
import rootStore from '~/store';
import LeftNavSection from '../common/LeftNav/LeftNavSection';
import NavItem from '../common/LeftNav/NavItem';
import type { ProjectDetailsParams } from '.';
import MapList from './civildraw/MapList';
import { EXPORT_TAB_ID } from './exportTabIds';
import store from './store';
import ReportsMovedNavItem from './utilitiesV2/ReportsMovedNavItem';
import { hasSeenReportsMoved } from './utilitiesV2/reportsMovedSeen';
import { useUtilitiesPageRedesignEnabled } from './utilitiesV2/useUtilitiesPageRedesignEnabled';

interface Props {
  project: Project;
  refetchProject: () => void;
}

export default function LeftNavComponent({ project, refetchProject }: Props) {
  const { tabId } = useParams({ strict: false }) as ProjectDetailsParams;
  const navigate = useNavigate();
  const projectId = project.id;
  const labels = useExportLabels();
  const { currentOrganization, me } = useSnapshot(rootStore) as typeof rootStore;
  const { civilSearchVersion } = useSnapshot(store) as typeof store;
  const reportType = currentOrganization?.mobileReportType;
  const permitPdfGenerationEnabled = usePermitPdfGenerationEnabled();
  const subscriptionConfig = currentOrganization?.subscription;
  const can = (feature: ProjectFeature) => hasFeatureAccess(feature, project.billingTier, subscriptionConfig);
  const { resolved: cnFlagResolved, enabled: cnFlagEnabled } = useConflictNotificationsFlag();
  const conflictNotificationsEnabled = cnFlagResolved && cnFlagEnabled;
  const canViewSummary = can(ProjectFeature.Summary);
  const isUtilitiesPageRedesignEnabled = useUtilitiesPageRedesignEnabled();
  // Read once per user, so Reports leaves on the next page load rather than under the cursor.
  const reportsMovedSeen = useMemo(() => hasSeenReportsMoved(me?.id), [me?.id]);

  useEffect(() => {
    // Only auto-redirect to summary when it's actually available, otherwise the project-root tab gate
    // would bounce it straight back here — an infinite redirect loop.
    if (
      (project.billingTier === 'draft' || project.billingTier === 'proposal') &&
      tabId === undefined &&
      canViewSummary
    ) {
      navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId: 'summary' } });
    }
  }, [navigate, project.billingTier, projectId, tabId, canViewSummary]);

  const { color, label } = returnProjectTypeSpecificProps(project.billingTier);

  const topSection = (
    <div className="sticky -top-2 bg-white">
      <div className="-mt-2 mb-4 bg-white p-1 flex gap-1">
        <div className={cn('w-1 shrink-0 rounded-full self-stretch', getBillingTierColorClass(project.billingTier))} />
        <div className="flex flex-col gap-1">
          <h1 className="font-noto text-md font-medium">{project.name}</h1>
          <div className="font-noto text-xs font-normal text-grey-90">PM: {project.projectManager.name}</div>
          <Badge color={color} className="w-fit capitalize">
            {label}
          </Badge>
        </div>
      </div>
      <div className="my-2 h-px border-b" />
    </div>
  );

  // Downloads lives in the General section; gated by the Downloads feature for the project's tier.
  // Shared across tiers so an org that enables Downloads for a lower tier actually gets the nav item.
  const downloadsItem = can(ProjectFeature.Downloads) ? (
    <Can key="downloads" action={Action.DownloadData} preventRender>
      <NavItem to={`/projects/${projectId}/${EXPORT_TAB_ID}`} data-testid="project-nav-exports">
        {labels.navItem}
      </NavItem>
    </Can>
  ) : null;

  // The Analysis section is built once from the resolver, then reused by every tier branch. This is what
  // lets a per-org override REVEAL a feature on a lower tier (not just hide one) — the previous hardcoded
  // per-tier item lists could only ever remove items, never surface a newly-enabled one.
  const utilitiesLabel = project.billingTier === 'proposal' ? 'Utilities Lite' : 'Utilities';
  const utilitiesChildItems = [
    // With the redesign, Reports only holds the "Reports have moved" notice, so it goes once that's been seen.
    ...(can(ProjectFeature.UtilitiesReports) && !(isUtilitiesPageRedesignEnabled && reportsMovedSeen)
      ? [
          {
            to: `/projects/${projectId}/utilities/reports`,
            label: 'Reports',
            'data-testid': 'project-nav-utilities-reports',
            // Points people to the Exports menu, where reports moved.
            element: isUtilitiesPageRedesignEnabled ? <ReportsMovedNavItem projectId={projectId} /> : undefined,
          },
        ]
      : []),
    ...(can(ProjectFeature.UtilitiesNotifications)
      ? [
          {
            to: `/projects/${projectId}/utilities/notifications`,
            label: 'Notifications',
            'data-testid': 'project-nav-utilities-notifications',
          },
        ]
      : []),
  ];

  const analysisItems: ReactNode[] = [];
  if (can(ProjectFeature.Utilities)) {
    analysisItems.push(
      <Can key="utilities" action={Action.ViewProjectDetails}>
        <NavItem
          to={`/projects/${projectId}/utilities`}
          data-testid="project-nav-utilities"
          showChildren={civilSearchVersion === 'v2' && utilitiesChildItems.length > 0}
          childItems={utilitiesChildItems}
        >
          {utilitiesLabel}
        </NavItem>
      </Can>,
    );
  }
  if (can(ProjectFeature.LandRights)) {
    analysisItems.push(
      <Can key="land-rights" action={Action.ViewProjectDetails}>
        <NavItem to={`/projects/${projectId}/land-rights`} data-testid="project-nav-land-rights">
          Land Rights
        </NavItem>
      </Can>,
    );
  }
  if (can(ProjectFeature.Zoning)) {
    analysisItems.push(
      <Can key="zoning" action={Action.ViewProjectDetails}>
        <NavItem to={`/projects/${projectId}/zoning`} data-testid="project-nav-zoning">
          Zoning
        </NavItem>
      </Can>,
    );
  }
  if (can(ProjectFeature.AntennaMap)) {
    analysisItems.push(
      <Can key="antenna-map" action={Action.ViewProjectDetails}>
        <NavItem to={`/projects/${projectId}/antenna-map`} data-testid="project-nav-antenna-map">
          Antenna Map
        </NavItem>
      </Can>,
    );
  }
  if (can(ProjectFeature.EnvironmentalStudies)) {
    analysisItems.push(
      <Can key="environmental-studies" action={Action.ViewProjectDetails}>
        <NavItem to={`/projects/${projectId}/environmental-studies`} data-testid="project-nav-environmental-studies">
          Environmental Study Data
        </NavItem>
      </Can>,
    );
  }
  if (can(ProjectFeature.Geotechnical)) {
    analysisItems.push(
      <Can key="geotechnical" action={Action.ViewProjectDetails}>
        <NavItem to={`/projects/${projectId}/geotechnical`} data-testid="project-nav-geotechnical">
          Geotechnical
        </NavItem>
      </Can>,
    );
  }
  if (can(ProjectFeature.Environmental)) {
    analysisItems.push(
      <Can key="environmental" action={Action.ViewProjectDetails}>
        <NavItem to={`/projects/${projectId}/environmental`} data-testid="project-nav-environmental">
          Environmental
        </NavItem>
      </Can>,
    );
  }
  if (can(ProjectFeature.FieldNotes)) {
    analysisItems.push(
      <Can key="field-notes" action={Action.ViewProjectDetails}>
        <NavItem to={`/projects/${projectId}/field-notes`} data-testid="project-nav-field-notes">
          Field Notes
        </NavItem>
      </Can>,
    );
  }
  if (can(ProjectFeature.Potholing)) {
    analysisItems.push(
      <Can key="potholing" action={Action.ViewProjectDetails}>
        <NavItem to={`/projects/${projectId}/potholing`} data-testid="project-nav-potholing">
          {reportType === 'manholeInspection' ? `Manhole Inspections` : `Potholing`}
        </NavItem>
      </Can>,
    );
  }
  if (can(ProjectFeature.Intelligence)) {
    analysisItems.push(
      <Can key="intelligence" action={Action.ViewProjectDetails}>
        <NavItem
          to={`/projects/${projectId}/intelligence`}
          data-testid="project-nav-intelligence"
          childItems={
            conflictNotificationsEnabled
              ? [
                  {
                    to: `/projects/${projectId}/intelligence/notifications`,
                    label: 'Notifications',
                    'data-testid': 'project-nav-intelligence-notifications',
                  },
                ]
              : []
          }
        >
          Intelligence
        </NavItem>
      </Can>,
    );
  }
  if (can(ProjectFeature.ProposalPacket)) {
    analysisItems.push(
      <Can key="proposal-packet" action={Action.ViewProjectDetails}>
        <NavItem to={`/projects/${projectId}/proposal-packet`} data-testid="project-nav-proposal-packet">
          Proposal Packet
        </NavItem>
      </Can>,
    );
  }
  if (permitPdfGenerationEnabled) {
    analysisItems.push(
      <Can key="permit-pdf-generation" action={Action.ViewProjectDetails}>
        <NavItem to={`/projects/${projectId}/permit-pdf-generation`}>Permit PDF Generation</NavItem>
      </Can>,
    );
  }
  const analysisSection =
    analysisItems.length > 0 ? <LeftNavSection title="Analysis">{analysisItems}</LeftNavSection> : null;

  if (project.billingTier === 'draft') {
    return (
      <LeftNav id="project-left-nav" aria-label="Project" backButtonState={{ from: 'projectDetails' }}>
        {topSection}
        <LeftNavSection title="General">
          {can(ProjectFeature.Summary) && (
            <Can action={Action.ViewProjectDetails}>
              <NavItem to={`/projects/${projectId}/summary`} data-testid="project-nav-summary">
                Summary
              </NavItem>
            </Can>
          )}
          <Can action={Action.ViewProjectDetails}>
            <NavItem to={`/projects/${projectId}/details`} data-testid="project-nav-details">
              <div className="flex items-center justify-start gap-2">
                Project Info <Badge color="orange">Upgrade</Badge>
              </div>
            </NavItem>
          </Can>
          <Can action={Action.ViewProjectDetails}>
            <NavItem to={`/projects/${projectId}/team`} data-testid="project-nav-team">
              Team
            </NavItem>
          </Can>
          <Can action={Action.ViewProjectDetails}>
            <NavItem to={`/projects/${projectId}/files-and-media`} data-testid="project-nav-files-and-media">
              Project Files
            </NavItem>
          </Can>
          {downloadsItem}
        </LeftNavSection>
        {analysisSection}
        <MapList />
      </LeftNav>
    );
  } else if (project.billingTier === 'proposal') {
    return (
      <LeftNav id="project-left-nav" aria-label="Project">
        {topSection}
        <LeftNavSection title="General">
          {can(ProjectFeature.Summary) && (
            <Can action={Action.ViewProjectDetails}>
              <NavItem to={`/projects/${projectId}/summary`} data-testid="project-nav-summary">
                Summary
              </NavItem>
            </Can>
          )}
          <Can action={Action.ViewProjectDetails}>
            <NavItem to={`/projects/${projectId}/details`} data-testid="project-nav-details">
              <div className="flex items-center justify-start gap-2">
                Project Info <Badge color="orange">Upgrade</Badge>
              </div>
            </NavItem>
          </Can>
          <Can action={Action.ViewProjectDetails}>
            <NavItem to={`/projects/${projectId}/team`} data-testid="project-nav-team">
              Team
            </NavItem>
          </Can>
          <Can action={Action.ViewProjectDetails}>
            <NavItem to={`/projects/${projectId}/files-and-media`} data-testid="project-nav-files-and-media">
              Project Files
            </NavItem>
          </Can>
          {can(ProjectFeature.Billing) && (
            <Can action={Action.ViewProjectTransactions}>
              <NavItem to={`/projects/${projectId}/billing`} data-testid="project-nav-billing">
                Billing
              </NavItem>
            </Can>
          )}
          {downloadsItem}
        </LeftNavSection>
        {analysisSection}
        <MapList />
      </LeftNav>
    );
  }

  return (
    <LeftNav id="project-left-nav" aria-label="Project">
      {topSection}

      <LeftNavSection title="General">
        <Can action={Action.ViewProjectDetails}>
          <NavItem to={`/projects/${projectId}/details`} data-testid="project-nav-details">
            Project Info
          </NavItem>
        </Can>
        <Can action={Action.ViewProjectDetails}>
          <NavItem to={`/projects/${projectId}/team`} data-testid="project-nav-team">
            Team
          </NavItem>
        </Can>
        {can(ProjectFeature.Billing) && (
          <Can action={Action.ViewProjectTransactions}>
            <NavItem to={`/projects/${projectId}/billing`} data-testid="project-nav-billing">
              Billing
            </NavItem>
          </Can>
        )}
        <Can action={Action.ViewProjectDetails}>
          <NavItem to={`/projects/${projectId}/files-and-media`} data-testid="project-nav-files-and-media">
            Project Files
          </NavItem>
        </Can>
        {downloadsItem}
      </LeftNavSection>

      {analysisSection}
      <MapList />
    </LeftNav>
  );
}
